import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, Calendar, Camera } from 'lucide-react'
import { listConsultations, listVaccinations } from '../../api/clinical'
import { listAppointments } from '../../api/appointments'
import { listMovements } from '../../api/stock'
import type { AppointmentStatus } from '../../types/appointments'
import { STATUS_COLORS, STATUS_LABELS as APPT_STATUS_LABELS } from '../../types/appointments'
import { getPatient } from '../../api/patients'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { useAuth } from '../../context/AuthContext'
import { useFetch } from '../../hooks/useFetch'
import {
  badgeBaseClass, crumbLinkClass, infoEmptyClass, infoLabelClass, infoValueClass,
  tbodyHoverClass, tdClass, thClass,
} from '../../lib/tableStyles'
import type { Patient, PatientOwner } from '../../types/patients'
import { SEX_LABELS, SPECIES_LABELS } from '../../types/patients'
import { getMyPractices } from '../../api/staff'
import PatientPhotoModal from './PatientPhotoModal'

const RELATIONSHIP_LABELS: Record<PatientOwner['relationship'], string> = {
  owner: 'Owner', co_owner: 'Co-owner', guardian: 'Guardian',
  foster: 'Foster', rescue_org: 'Rescue Org',
}
const STATUS_LABELS: Record<PatientOwner['status'], string> = {
  active: 'Active', pending_transfer: 'Pending Transfer', transferred: 'Transferred',
}

function calcAge(dob: string): string {
  const birth = new Date(dob)
  const now = new Date()
  const months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth())
  if (months < 1) return '< 1 month'
  if (months < 24) return `${months} month${months !== 1 ? 's' : ''}`
  const years = Math.floor(months / 12)
  return `${years} year${years !== 1 ? 's' : ''}`
}

export default function PatientDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const canEditClinical = user?.role !== 'org_admin'
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  const [photoOverride, setPhotoOverride] = useState<Patient | null>(null)

  const { data: patientData, loading, error } = useFetch(
    () => getPatient(Number(id)),
    [id],
  )
  const patient = photoOverride ?? patientData

  const { data: consultations } = useFetch(
    () => listConsultations(Number(id), { limit: 50 }),
    [id],
  )

  const { data: vaccinations } = useFetch(
    () => listVaccinations(Number(id)),
    [id],
  )

  const { data: appointments } = useFetch(
    () => listAppointments({ patient_id: Number(id), limit: 50 }),
    [id],
  )

  const { data: practices } = useFetch(() => getMyPractices(), [])
  const practiceId = practices?.[0]?.id

  const { data: drugHistory } = useFetch(
    () => practiceId ? listMovements({ practice_id: practiceId, movement_type: 'dispense' }) : Promise.resolve([]),
    [practiceId],
  )
  const patientDrugHistory = (drugHistory ?? []).filter(m => m.patient_id === Number(id))

  if (loading) return <div className="text-text-2 py-8">Loading…</div>

  if (error || !patient) {
    return (
      <div>
        <ErrorBox>{error ?? 'Patient not found.'}</ErrorBox>
        <button
          className="bg-transparent border-none cursor-pointer text-accent font-medium text-[0.9rem] p-0"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>
    )
  }

  const primaryOwner = patient.owners.find((o) => o.is_primary) ?? patient.owners[0] ?? null
  const editUrl = primaryOwner ? `/clients/${primaryOwner.client_id}/patients/${patient.id}/edit` : null
  const desexedLabel = patient.is_desexed === true ? 'Yes' : patient.is_desexed === false ? 'No' : 'Unknown'

  return (
    <div>
      <nav className="text-[0.85rem] text-text-3 mb-5 flex items-center gap-[0.4rem]">
        <Link to="/clients" className={crumbLinkClass}>Clients</Link>
        {primaryOwner && (
          <>
            <span>›</span>
            <Link to={`/clients/${primaryOwner.client_id}`} className={crumbLinkClass}>
              {primaryOwner.first_name} {primaryOwner.last_name}
            </Link>
          </>
        )}
        <span>›</span>
        <span>{patient.name}</span>
      </nav>

      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex items-center gap-4">
          {/* Photo avatar */}
          <button
            onClick={() => setShowPhotoModal(true)}
            className="relative group w-[72px] h-[72px] rounded-full overflow-hidden border-2 border-border shrink-0 bg-surface-alt cursor-pointer p-0"
            aria-label="Change patient photo"
          >
            {patient.photo_url ? (
              <img src={patient.photo_url} alt={patient.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-text-4">
                <Camera size={28} strokeWidth={1.5} />
              </div>
            )}
            <div className="absolute inset-0 bg-black/45 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera size={20} className="text-white" />
            </div>
          </button>
          <div>
            <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">{patient.name}</h1>
            <p className="mt-[0.2rem] mb-0 text-[0.9rem] text-text-3">
              {SPECIES_LABELS[patient.species]}
              {patient.breed ? ` · ${patient.breed}` : ''}
              {patient.date_of_birth ? ` · ${calcAge(patient.date_of_birth)} old` : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {editUrl && <ButtonLink to={editUrl}>Edit</ButtonLink>}
          <Button variant="outline" onClick={() => navigate(-1)}>← Back</Button>
        </div>
      </div>

      {patient.allergies && (
        <div className="bg-danger-bg border border-danger-border rounded-md px-4 py-3 mb-5 flex items-start gap-[0.6rem] text-sm">
          <AlertTriangle size={16} strokeWidth={1.75} className="text-danger-text shrink-0 mt-[1px]" />
          <div>
            <span className="font-bold text-danger-text text-[0.875rem]">Allergies: </span>
            <span className="text-danger-text text-[0.875rem]">{patient.allergies}</span>
          </div>
        </div>
      )}

      {(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const overdue = (vaccinations ?? []).filter(v => v.next_due_date && new Date(v.next_due_date) < today)
        if (overdue.length === 0) return null
        return (
          <div className="bg-warning-bg border border-warning-border rounded-md px-4 py-3 mb-5 flex items-start gap-[0.6rem] text-sm">
            <Calendar size={14} strokeWidth={1.75} className="text-warning-text shrink-0 mt-[1px]" />
            <div>
              <span className="font-bold text-warning-text text-[0.875rem]">Vaccinations overdue: </span>
              <span className="text-warning-text text-[0.875rem]">
                {overdue.map(v => `${v.vaccine_name} (due ${new Date(v.next_due_date!).toLocaleDateString()})`).join(', ')}
              </span>
            </div>
          </div>
        )
      })()}

      <div className="grid gap-5">
        {/* Patient Details */}
        <Card>
          <SectionTitle>Patient Details</SectionTitle>
          <div className="grid grid-cols-2 gap-y-[0.85rem] gap-x-8 max-sm:grid-cols-1">
            <div><div className={infoLabelClass}>Species</div><div className={infoValueClass}>{SPECIES_LABELS[patient.species]}</div></div>
            <div><div className={infoLabelClass}>Breed</div><div className={patient.breed ? infoValueClass : infoEmptyClass}>{patient.breed ?? 'Not specified'}</div></div>
            <div><div className={infoLabelClass}>Sex</div><div className={infoValueClass}>{SEX_LABELS[patient.sex]}</div></div>
            <div><div className={infoLabelClass}>Desexed</div><div className={infoValueClass}>{desexedLabel}</div></div>
            <div>
              <div className={infoLabelClass}>Date of birth</div>
              <div className={patient.date_of_birth ? infoValueClass : infoEmptyClass}>
                {patient.date_of_birth
                  ? `${new Date(patient.date_of_birth).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })} (${calcAge(patient.date_of_birth)})`
                  : 'Not specified'}
              </div>
            </div>
            <div>
              <div className={infoLabelClass}>Weight</div>
              <div className={patient.weight_kg != null ? infoValueClass : infoEmptyClass}>
                {patient.weight_kg != null ? `${patient.weight_kg} kg` : 'Not specified'}
              </div>
            </div>
            <div><div className={infoLabelClass}>Colour</div><div className={patient.color ? infoValueClass : infoEmptyClass}>{patient.color ?? 'Not specified'}</div></div>
            <div><div className={infoLabelClass}>Microchip</div><div className={patient.microchip_number ? infoValueClass : infoEmptyClass}>{patient.microchip_number ?? 'Not recorded'}</div></div>
          </div>
          {patient.allergies && (
            <div className="mt-5">
              <div className={infoLabelClass}>Allergies</div>
              <div className="text-[0.925rem] whitespace-pre-wrap text-danger-text mt-[0.2rem]">{patient.allergies}</div>
            </div>
          )}
          {patient.notes && (
            <div className="mt-5">
              <div className={infoLabelClass}>Notes</div>
              <div className="text-[0.925rem] whitespace-pre-wrap text-text-2 mt-[0.2rem]">{patient.notes}</div>
            </div>
          )}
          {consultations && consultations.length > 0 && (
            <div className="mt-5">
              <div className={infoLabelClass}>Last visit</div>
              <div className={infoValueClass}>
                {new Date(Math.max(...consultations.map(c => new Date(c.date).getTime()))).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>
          )}
        </Card>

        {/* Clinical History */}
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between p-5 pb-0">
            <SectionTitle style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
              Consultation History ({consultations?.length ?? 0})
            </SectionTitle>
            {canEditClinical && (
              <ButtonLink to={`/patients/${patient.id}/consultations/new`} size="sm">+ New Consultation</ButtonLink>
            )}
          </div>
          {!consultations || consultations.length === 0 ? (
            <div className="text-center py-8 px-4 text-text-3 p-5">
              <div className="text-sm mb-3">No consultations recorded yet</div>
              {canEditClinical && (
                <ButtonLink to={`/patients/${patient.id}/consultations/new`} size="sm">+ Start Consultation</ButtonLink>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full border-collapse text-[0.875rem]">
                <thead>
                  <tr>
                    <th className={thClass}>Date</th>
                    <th className={thClass}>Reason</th>
                    <th className={thClass}>Vet</th>
                    <th className={thClass}>Status</th>
                    <th className={`${thClass} text-right`}>View</th>
                  </tr>
                </thead>
                <tbody className={tbodyHoverClass}>
                  {consultations.map((c) => (
                    <tr key={c.id}>
                      <td className={`${tdClass} whitespace-nowrap`}>{new Date(c.date).toLocaleDateString()}</td>
                      <td className={`${tdClass} text-text-2`}>{c.reason ?? '—'}</td>
                      <td className={`${tdClass} text-text-2`}>{c.veterinarian_name ?? '—'}</td>
                      <td className={tdClass}>
                        <span
                          className={badgeBaseClass}
                          style={{
                            color: c.status === 'completed' ? 'var(--success-text)' : 'var(--warning-text)',
                            background: c.status === 'completed' ? 'var(--success-bg)' : 'var(--warning-bg)',
                          }}
                        >
                          {c.status === 'completed' ? 'Completed' : 'Open'}
                        </span>
                      </td>
                      <td className={`${tdClass} text-right`}>
                        <Link to={`/consultations/${c.id}`} className="text-accent font-medium text-[0.82rem] no-underline hover:underline">
                          Open →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Appointments */}
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between p-5 pb-0">
            <SectionTitle style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
              Appointments ({appointments?.length ?? 0})
            </SectionTitle>
            <ButtonLink to={`/patients/${patient.id}/appointments/new`} size="sm">+ New Appointment</ButtonLink>
          </div>
          {!appointments || appointments.length === 0 ? (
            <div className="text-center py-8 px-4 text-text-3 p-5">
              <div className="text-sm mb-3">No appointments scheduled yet</div>
              <ButtonLink to={`/patients/${patient.id}/appointments/new`} size="sm">+ Book Appointment</ButtonLink>
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full border-collapse text-[0.875rem]">
                <thead>
                  <tr>
                    <th className={thClass}>Date &amp; Time</th>
                    <th className={thClass}>Reason</th>
                    <th className={thClass}>Vet</th>
                    <th className={thClass}>Status</th>
                    <th className={`${thClass} text-right`}>View</th>
                  </tr>
                </thead>
                <tbody className={tbodyHoverClass}>
                  {appointments.map((a) => {
                    const { color, bg } = STATUS_COLORS[a.status as AppointmentStatus]
                    return (
                      <tr key={a.id}>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {new Date(a.scheduled_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className={`${tdClass} text-text-2`}>{a.reason ?? '—'}</td>
                        <td className={`${tdClass} text-text-2`}>{a.veterinarian_name ?? '—'}</td>
                        <td className={tdClass}>
                          <span
                            className={badgeBaseClass}
                            style={{ color, background: bg }}
                          >
                            {APPT_STATUS_LABELS[a.status as AppointmentStatus]}
                          </span>
                        </td>
                        <td className={`${tdClass} text-right`}>
                          <Link to={`/appointments/${a.id}`} className="text-accent font-medium text-[0.82rem] no-underline hover:underline">
                            View →
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Vaccination History */}
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between p-5 pb-0">
            <SectionTitle style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
              Vaccination History ({vaccinations?.length ?? 0})
            </SectionTitle>
            <div className="flex gap-2">
              {!!vaccinations?.length && (
                <Button
                  variant="outline" size="sm"
                  onClick={() => window.open(`/patients/${patient.id}/vaccination-certificate`, '_blank')}
                >
                  Print Certificate
                </Button>
              )}
              {canEditClinical && (
                <ButtonLink to={`/patients/${patient.id}/vaccinations/new`} size="sm">+ Record Vaccination</ButtonLink>
              )}
            </div>
          </div>
          {!vaccinations || vaccinations.length === 0 ? (
            <div className="text-center py-8 px-4 text-text-3 p-5">
              <div className="text-sm mb-3">No vaccinations recorded yet</div>
              {canEditClinical && (
                <ButtonLink to={`/patients/${patient.id}/vaccinations/new`} size="sm">+ Record Vaccination</ButtonLink>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full border-collapse text-[0.875rem]">
                <thead>
                  <tr>
                    <th className={thClass}>Vaccine</th>
                    <th className={thClass}>Date Given</th>
                    <th className={thClass}>Next Due</th>
                    <th className={thClass}>Batch No.</th>
                  </tr>
                </thead>
                <tbody className={tbodyHoverClass}>
                  {vaccinations.map((v) => (
                    <tr key={v.id}>
                      <td className={`${tdClass} font-semibold`}>{v.vaccine_name}</td>
                      <td className={`${tdClass} text-text-2`}>{new Date(v.date_given).toLocaleDateString()}</td>
                      <td className={`${tdClass} ${v.next_due_date && new Date(v.next_due_date) < new Date() ? 'text-danger-text' : 'text-text-2'}`}>
                        {v.next_due_date ? new Date(v.next_due_date).toLocaleDateString() : '—'}
                      </td>
                      <td className={`${tdClass} text-text-2`}>{v.batch_number ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Drug History */}
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between p-5 pb-0">
            <SectionTitle style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
              Drug History
            </SectionTitle>
            {practiceId && (
              <Link to="/stock/register" className="text-accent font-medium text-[0.82rem] no-underline hover:underline">
                View full register →
              </Link>
            )}
          </div>
          {patientDrugHistory.length === 0 ? (
            <div className="text-center py-6 px-4 text-text-3 text-[0.875rem] p-5">
              No drugs dispensed to this patient
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full border-collapse text-[0.875rem]">
                <thead>
                  <tr>
                    <th className={thClass}>Date</th>
                    <th className={thClass}>Batch</th>
                    <th className={thClass}>Qty</th>
                    <th className={thClass}>Balance after</th>
                    <th className={thClass}>Consultation</th>
                  </tr>
                </thead>
                <tbody className={tbodyHoverClass}>
                  {patientDrugHistory.slice(0, 20).map(m => (
                    <tr key={m.id}>
                      <td className={`${tdClass} whitespace-nowrap`}>{new Date(m.movement_date).toLocaleDateString()}</td>
                      <td className={`${tdClass} text-text-2`}>#{m.stock_batch_id}</td>
                      <td className={tdClass}>{m.quantity}</td>
                      <td className={tdClass}>{m.balance_after}</td>
                      <td className={tdClass}>
                        {m.consultation_id
                          ? <Link to={`/consultations/${m.consultation_id}`} className="text-accent no-underline text-[0.82rem] hover:underline">View →</Link>
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Owners */}
        <Card className="overflow-hidden p-0">
          <div className="p-5 pb-0">
            <SectionTitle>Owners ({patient.owners.length})</SectionTitle>
          </div>
          {patient.owners.length === 0 ? (
            <div className="text-center py-8 px-4 text-text-3 p-5">No owners recorded</div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full border-collapse text-[0.9rem]">
                <thead>
                  <tr>
                    <th className={thClass}>Name</th>
                    <th className={thClass}>Relationship</th>
                    <th className={thClass}>Primary</th>
                    <th className={thClass}>Status</th>
                  </tr>
                </thead>
                <tbody className={tbodyHoverClass}>
                  {patient.owners.map((owner) => (
                    <tr key={owner.id}>
                      <td className={`${tdClass} font-semibold`}>
                        <Link to={`/clients/${owner.client_id}`} className={crumbLinkClass}>
                          {owner.first_name} {owner.last_name}
                        </Link>
                      </td>
                      <td className={tdClass}>{RELATIONSHIP_LABELS[owner.relationship]}</td>
                      <td className={tdClass}>
                        {owner.is_primary && (
                          <span
                            className={badgeBaseClass}
                            style={{ color: 'var(--success-text)', background: 'var(--success-bg)' }}
                          >
                            Primary
                          </span>
                        )}
                      </td>
                      <td className={tdClass}>
                        <span
                          className={badgeBaseClass}
                          style={{
                            color: owner.status === 'active' ? 'var(--success-text)' : 'var(--warning-text)',
                            background: owner.status === 'active' ? 'var(--success-bg)' : 'var(--warning-bg)',
                          }}
                        >
                          {STATUS_LABELS[owner.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {showPhotoModal && (
        <PatientPhotoModal
          patient={patient}
          onClose={() => setShowPhotoModal(false)}
          onUpdated={updated => {
            setPhotoOverride(updated)
            setShowPhotoModal(false)
          }}
        />
      )}
    </div>
  )
}
